import { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import ResourcePersonNavBar from "../components/ResourcePersonNavBar";
import { useResourcePersonBankDetails } from "../store/resourcePerson/resourcePersonStore";

const EMPTY_FORM = { bank_code: "", bank_name: "", account_number: "" };

/**
 * Bank details page - resource-person payout account.
 *
 *   GET   /api/resource-person/bank-details
 *   PATCH /api/resource-person/bank-details
 *   GET   /api/resource-person/banks
 */
export default function BankDetails() {
  const store = useResourcePersonBankDetails();
  const [form, setForm] = useState(EMPTY_FORM);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);

  const details = store.bankDetails;

  useEffect(() => {
    store.fetchBankDetails();
    store.fetchBanks();
  }, []);

  // Seed the form from the masked payload returned by GET /bank-details.
  useEffect(() => {
    if (!details || editing) return;
    setForm({
      bank_code: details.bank_code || details.bankCode || "",
      bank_name: details.bank_name || details.bankName || "",
      account_number: "",
    });
  }, [details, editing]);

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleBankPick = (event) => {
    const index = Number(event.target.value);
    const picked = bankOptions[index];
    if (!picked) return;
    setForm((prev) => ({
      ...prev,
      bank_code: picked.code,
      bank_name: picked.name || prev.bank_name,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    if (!form.account_number) {
      setError("Enter your account number so the bank can be verified.");
      return;
    }

    if (!form.bank_name.trim()) {
      setError("Choose your bank first.");
      return;
    }

    // The API expects camelCase body fields (`bankName`, `accountNumber`).
    // It resolves the account name through Paystack and returns it.
    const payload = {
      bankName: form.bank_name.trim(),
      accountNumber: form.account_number.trim(),
    };

    const result = details
      ? await store.updateBankDetails(payload)
      : await store.createBankDetails(payload);

    if (!result.success) {
      setError(result.error || "Failed to save bank details");
      return;
    }

    setEditing(false);
    setMessage("Bank details saved");
    setForm((prev) => ({ ...prev, account_number: "" }));
  };

  /**
   * The banks payload can repeat a bank once per payment-provider recipient, so
   * codes (and ids) are not unique. Dedupe by code+name and key options by their
   * index — a `value`/`key` that repeats would make rows unselectable, not just
   * noisy in the console.
   */
  const bankOptions = useMemo(() => {
    const seen = new Map();
    for (const bank of store.banks) {
      const code = String(bank?.code ?? bank?.bank_code ?? bank?.backend_code ?? "").trim();
      const name = String(
        bank?.name || bank?.bank_name || bank?.bankName || (code ? "" : "Unnamed bank")
      ).trim();
      const id = `${code}::${name}`;
      if (seen.has(id)) continue;
      seen.set(id, { code, name });
    }
    return [...seen.values()];
  }, [store.banks]);

  // Keep the saved bank selected after a reload (form.bank_code is what
  // GET /bank-details returns; the option values are indices).
  const selectedBankIndex = useMemo(() => {
    if (!form.bank_code) return "";
    const index = bankOptions.findIndex(
      (bank) =>
        bank.code === String(form.bank_code) ||
        (form.bank_name && bank.name === String(form.bank_name))
    );
    return index === -1 ? "" : String(index);
  }, [bankOptions, form.bank_code, form.bank_name]);

  const refresh = () => {
    setError("");
    store.fetchBankDetails();
    store.fetchBanks();
  };

  const rawError = error || store.bankDetailsError || store.bankMutationError;
  const displayError = /failed to fetch|network|load failed/i.test(String(rawError || ""))
    ? "We couldn't load your bank details. Check your connection and try again."
    : rawError;

  return (
    <div className="min-h-screen bg-stone-50 font-sans text-stone-900">
      <ResourcePersonNavBar title="Bank Details" subTitle="Payouts" />

      <main className="mx-auto max-w-4xl px-5 py-10 pt-24">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-blue-700">Payout bank account</h1>
            <p className="mt-1 text-sm text-stone-500">
              Withdrawals are paid into this account. Numbers are stored masked.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={refresh}
              disabled={store.bankDetailsLoading || store.banksLoading}
              title="Refresh bank details"
              aria-label="Refresh bank details"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-600 transition hover:bg-stone-100 disabled:opacity-60"
            >
              <RefreshCw
                className={`h-4 w-4 ${
                  store.bankDetailsLoading || store.banksLoading ? "animate-spin" : ""
                }`}
              />
            </button>
            {!store.bankDetailsLoading && (
              <button
                type="button"
                onClick={() => { setEditing(true); setMessage(""); setError(""); }}
                disabled={editing}
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {details ? "Update bank details" : "Create bank details"}
              </button>
            )}
          </div>
        </div>

        {message && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
            {message}
          </div>
        )}
        {displayError && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{displayError}</span>
            <button
              type="button"
              onClick={refresh}
              disabled={store.bankDetailsLoading || store.banksLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-60"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${
                  store.bankDetailsLoading || store.banksLoading ? "animate-spin" : ""
                }`}
              />
              Refresh
            </button>
          </div>
        )}
        <section className="mt-6 rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-base font-bold text-blue-700">Saved details</h2>

          {store.bankDetailsLoading ? (
            <div className="flex justify-center py-10">
              <div className="loader" />
            </div>
          ) : details ? (
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-semibold uppercase text-stone-400">Bank</dt>
                <dd className="text-sm text-stone-700">
                  {details.bank_name || details.bankName || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase text-stone-400">Account name</dt>
                <dd className="text-sm text-stone-700">
                  {details.account_name || details.accountName || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase text-stone-400">Account number</dt>
                <dd className="text-sm text-stone-700">
                  {details.account_number_masked ||
                    details.masked_account_number ||
                    details.account_number ||
                    details.accountNumber ||
                    "—"}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-stone-400">
              No bank account on file yet. Add one to enable withdrawals.
            </p>
          )}
        </section>

        {editing && <form
          onSubmit={handleSubmit}
          className="mt-6 rounded-2xl border border-blue-100 bg-white p-5 shadow-sm"
        >
          <h2 className="mb-4 text-base font-bold text-blue-700">
            {details ? "Update bank account" : "Add bank account"}
          </h2>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-xs font-semibold text-stone-600">
                Bank {store.banksLoading ? "(loading…)" : ""}
              </span>
              <select
                required
                value={selectedBankIndex}
                onChange={handleBankPick}
                className="rounded-lg border border-blue-100 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
              >
                <option value="">Select a supported bank</option>
                {bankOptions.map((bank, index) => (
                  <option key={index} value={index}>
                    {bank.name ? `${bank.name}${bank.code ? ` (${bank.code})` : ""}` : bank.code}
                  </option>
                ))}
              </select>
              {store.banksError && (
                <span className="text-xs text-red-600">{store.banksError}</span>
              )}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-stone-600">Bank name</span>
              <input
                type="text"
                value={form.bank_name}
                onChange={setField("bank_name")}
                className="rounded-lg border border-blue-100 px-3 py-2 text-sm outline-none focus:border-blue-400"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-stone-600">Account number</span>
              <input
                required
                type="text"
                inputMode="numeric"
                value={form.account_number}
                onChange={setField("account_number")}
                placeholder="10 digits"
                className="rounded-lg border border-blue-100 px-3 py-2 text-sm outline-none focus:border-blue-400"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-stone-600">Account name</span>
              <input
                type="text"
                value={details?.accountName || details?.account_name || "Resolved by Paystack after saving"}
                readOnly
                className="rounded-lg border border-blue-100 bg-stone-50 px-3 py-2 text-sm text-stone-500 outline-none"
              />
            </label>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              disabled={store.bankMutationLoading}
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:opacity-60"
            >
              {store.bankMutationLoading ? "Saving…" : "Save bank details"}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setError(""); }}
              className="rounded-lg bg-stone-100 px-4 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-200"
            >
              Cancel
            </button>
          </div>
        </form>}

      </main>
    </div>
  );
}
